import { Injectable, computed, signal } from '@angular/core';
import { OptionsTabs, Tabs } from '../models/tab';

@Injectable({
  providedIn: 'root'
})
export class CartoafTabService {

  private _tabs = signal<Tabs>({});
  private _activeTab = signal<string | null>(null);

  readonly tabs = this._tabs.asReadonly();
  readonly activeTab = this._activeTab.asReadonly();
  readonly keys = computed(() => Object.keys(this._tabs()));

  push(key: string, content: OptionsTabs, active: boolean = true) {
    if (content.type === 'dashboard') {
      this._tabs.set({ [key]: content, ...this._tabs() });
    } else {
      this._tabs.update(tabs => ({ ...tabs, [key]: content }));
    }
    sessionStorage.setItem('cartoafTabs', JSON.stringify(this.keys()));
    if (active) {
      this.setActiveTab(key);
    }
  }

  removeTab(key: string) {
    if (!this.hasTab(key)) return;
    const isActive = key === this._activeTab();
    if (isActive) {
      const nextKey = this.keys().filter(k => k !== key).at(-1) ?? null;
      this._activeTab.set(nextKey);
      if (nextKey) sessionStorage.setItem('cartoafActiveTab', nextKey);
    }
    // Defer removal so ng-bootstrap processes the active-tab switch in its own
    // CD cycle before the nav item disappears, preventing a stale content pane.
    setTimeout(() => {
      this._tabs.update(tabs => {
        const copy = { ...tabs };
        delete copy[key];
        return copy;
      });
      sessionStorage.setItem('cartoafTabs', JSON.stringify(this.keys()));
    });
  }

  hasTab(key: string) {
    return key in this._tabs();
  }

  getTab(key: string) {
    return this.hasTab(key) ? this._tabs()[key] : null;
  }

  setActiveTab(key: string) {
    if (!this.hasTab(key)) return;
    this._activeTab.set(key);
    sessionStorage.setItem('cartoafActiveTab', key);
  }

  clearTabs(type: string | null) {
    const current = this._tabs();
    const filtered = Object.keys(current)
      .filter(key => type ? key.split('_')[0] !== type : key === 'home')
      .reduce((acc, key) => ({ ...acc, [key]: current[key] }), {} as Tabs);
    this._tabs.set(filtered);
    sessionStorage.setItem('cartoafTabs', JSON.stringify(Object.keys(filtered)));
    const keys = Object.keys(filtered);
    if (keys.length > 0) {
      this.setActiveTab(keys[keys.length - 1]);
    }
  }

  size() {
    return this.keys().length;
  }
}
